"use client";
import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Link from "next/link";
import { EmblaCarouselType } from "embla-carousel/components/EmblaCarousel";
import { VariantCard } from "../components/Listing";
import { PropertyType } from "../../../../../types";
import Pagination from "./Pagination";
// Ensure you have relevant styles
export type UseDotButtonType = {
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
};
export const useDotButton = (
  emblaApi: EmblaCarouselType | undefined
): UseDotButtonType => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};
// Define the type for announcement data
export interface Announcement {
  title: string;
  description: string;
  image: string; // URL or path to the image
  // Optional button text
}
// Sample announcement data (replace with your actual data)
const SimilarProperties: React.FC<{
  arr?: PropertyType[];
}> = ({ arr }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: "auto",
  });

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  if (!arr) {
    return <div>Staff listing is not available</div>;
  }

  return (
    <section className="py-10">
      <div className="  mx-auto">
        <div className="relative">
          <div className="first flex justify-between mb-8">
            <h2 className=" font-medium text-primary">Staff Listing</h2>
            <Link
              href="/properties"
              className="text-themeColor underline-offset-2
               underline"
            >
              See More Properties
            </Link>
          </div>

          {/* Custom Prev Button */}
          <button
            className={`absolute rotate-180  top-1/2 transform -translate-y-1/2 z-10 ${
              canScrollPrev ? "visible" : "invisible"
            }`}
            onClick={() => emblaApi && emblaApi.scrollPrev()}
          >
            <ButtonControls />
          </button>
          {/* Embla Carousel */}
          <div className="w-full">
            <div className="embla overflow-hidden" ref={emblaRef}>
              <div className="embla__container items-stretch flex gap-5">
                {arr.map((listing, index) => (
                  <div
                    className="embla__slide select-none shrink-0"
                    key={index}
                  >
                    <VariantCard {...listing} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="pag mt-5 flex justify-center">
            <Pagination
              onDotButtonClick={onDotButtonClick}
              selectedIndex={selectedIndex}
              scrollSnaps={scrollSnaps}
            />
          </div>
          {/* Custom Next Button */}
          <button
            className={`absolute top-1/2 right-4 transform -translate-y-1/2 z-10  ${
              canScrollNext ? "visible" : "invisible"
            }`}
            onClick={() => emblaApi && emblaApi.scrollNext()}
          >
            <ButtonControls />
          </button>
        </div>

        {/* <Button href="/" className="w-max mx-auto block mt-8">
          Explore More
        </Button> */}
      </div>
    </section>
  );
};
export default SimilarProperties;

export const ButtonControls = () => {
  return (
    <svg
      width="68"
      height="68"
      viewBox="0 0 68 68"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g filter="url(#filter0_d_1580_62305)">
        <circle
          cx="24"
          cy="24"
          r="24"
          transform="matrix(-1 0 0 1 58 8)"
          fill="white"
        />
        <path
          d="M28.7344 22.0235C28.6262 21.909 28.5416 21.7743 28.4855 21.6271C28.4293 21.4799 28.4027 21.3231 28.4072 21.1657C28.4116 21.0082 28.447 20.8532 28.5114 20.7094C28.5758 20.5656 28.6679 20.4359 28.7824 20.3277C28.8969 20.2195 29.0315 20.1349 29.1787 20.0788C29.3259 20.0226 29.4827 19.996 29.6402 20.0005C29.7977 20.0049 29.9527 20.0404 30.0965 20.1047C30.2403 20.1691 30.37 20.2612 30.4782 20.3757L40.6722 31.1693C40.8827 31.392 41 31.6868 41 31.9933C41 32.2997 40.8827 32.5945 40.6722 32.8172L30.4782 43.612C30.3707 43.729 30.241 43.8235 30.0967 43.89C29.9524 43.9565 29.7963 43.9936 29.6375 43.9992C29.4788 44.0049 29.3204 43.9789 29.1718 43.9229C29.0231 43.8668 28.8871 43.7818 28.7716 43.6727C28.6561 43.5636 28.5634 43.4326 28.4989 43.2874C28.4344 43.1422 28.3994 42.9857 28.396 42.8268C28.3925 42.668 28.4206 42.51 28.4787 42.3621C28.5368 42.2143 28.6237 42.0794 28.7344 41.9654L38.1513 31.9933L28.7344 22.0235Z"
          fill="var(--primary-color)"
        />
      </g>
      <defs>
        <filter
          id="filter0_d_1580_62305"
          x="0"
          y="0"
          width="68"
          height="68"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="2" />
          <feGaussianBlur stdDeviation="5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.129412 0 0 0 0 0.129412 0 0 0 0 0.129412 0 0 0 0.15 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_1580_62305"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_1580_62305"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
};
